import { ChangeDetectorRef, Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import S3 from 'aws-sdk/clients/s3';
import { environment } from '@environments/environment';
import { Application } from '@enums/application.enums';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CommonService } from '@modules/shared/services/common.service';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ApiCallService } from './api-call.service';
import { API_URLS } from '@core/api-url.enum';
import Compressor from 'compressorjs';
import { REGEX } from '@core/validation';
import { topFanD } from '@utils/common.utils';
import { ErrorCodes } from '@enums/comman.enums';

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  fileSizeMax = Number(
    Application.ONE *
      Application.ONE_THOUSAND_TWENTY_FOUR *
      Application.ONE_THOUSAND_TWENTY_FOUR
  );
  widthHeightMax = Number(Application.ONE_THOUSAND_TWENTY_FOUR);
  defaultWidthHeightRatio = Number(Application.DEFAULT_QUALITY);
  defaultQualityRatio = Number(Application.DEFAULT_QUALITY);
  defaultSize = Number(
    Application.THREE * Application.ONE_THOUSAND_TWENTY_FOUR
  );

  constructor(
    private readonly cs: CommonService,
    @Inject(PLATFORM_ID) private platformId,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly apiCallService: ApiCallService,
    private ngZone: NgZone,
  ) {}

  get awsConfig() {
    const sData = topFanD(environment.sKey) || [];
    return new S3({
      accessKeyId: sData[0],
      secretAccessKey: sData[1],
      region: environment.s3Config.region,
      maxRetries: 0,
      httpOptions: {
        timeout: 1 * 60 * 60 * 1000, // 60 minutes timeout for large video uploads
        connectTimeout: 1 * 60 * 1000 // 60 seconds to establish connection
      }
    });
  }

  uploadFiles(file, contentDisposition = false) {
    if (!file) {
      return
    }
    const { height, width, apply_watermark, user_id, text, location, free_preview_time, preview_required, media_id, content_type, duration } = file.metadata || {};
    const params: S3.PutObjectRequest = {
      Bucket: environment.s3Config.bucketName,
      Key: file.folder + file.fileName,
      Body: file.file,
      ContentType: file.file?.type,
      Metadata: {
        width: width || '',
        height: height || '',
        apply_watermark: String(Boolean(apply_watermark)),
        user_id: user_id || '',
        watermark_value: text || '',
        location: location || '',
        free_preview_time: free_preview_time || '',
        preview_required: preview_required || '',
        media_id: media_id || '',
        content_type: content_type || '',
        duration: duration || ''
      }
    };

    if (contentDisposition) {
      const originalFileName = file.fileName.replace(
        REGEX.PREFIX_POST_IMAGE,
        ''
      );
      params.ContentDisposition = `attachment; filename=${originalFileName}`;
    }

    const managedUpload = this.awsConfig.upload(params);

    managedUpload.send((err) => {
      if (err) {
        managedUpload.abort();
        return false;
      }
      return true;
    });
    return managedUpload;
  }

  uploadFile(file): any {
    const params: S3.PutObjectRequest = {
      Bucket: environment.s3Config.bucketName,
      Key: file.folder + file.fileName,
      Body: file.file,
      ContentType: file?.file?.type
    };
    if (file.metadata) {
      params.Metadata = {
        ...file.metadata,
      };
    }
    return new Promise((resolve, reject) => {
      this.awsConfig.upload(params, (err, resp) => {
        if (err) {
          return reject(err);
        } else {
          return resolve({ data: resp });
        }
      });
    });
  }
  async checkAndConvertHeicOrAvifToJpeg(file) {
    try {
      if (
        file.name.toUpperCase().includes('.HEIC') ||
        file.name.toUpperCase().includes('.HEIF')
      ) {
        file = await this.convertHeicToJpeg(file);
      } else if (
        file.name.toUpperCase().includes('.AVIF')
      ) {
        file = await this.convertAvifToJpeg(file);
      }
      return file;
    } catch (err) {
      this.cs.showErrorMessage('Something went wrong. Please try again.');
      throw err;
    }
  }

  uploadImageCompress(file, quality = Application.ZERO_POINT_FOUR as number, cdr?: ChangeDetectorRef) {
    return new Promise(async (resolve, reject) => {
      try {
        if (file.type === Application.IMAGE_GIF_TYPE) {
          return resolve(file);
        }
        file = await this.checkAndConvertHeicOrAvifToJpeg(file);

        this.ngZone.run(() => {
          // 🔴 Run Compressor inside Angular zone
          new Compressor(file, {
            quality: quality,
            success: (result) => {
              cdr?.detectChanges();
              const compressedFile = new File([result], file.name, {
                type: result.type,
                lastModified: file.lastModified
              });
              resolve(compressedFile);
            },
            error: (err) => {
              cdr?.detectChanges();
              reject(err);
            }
          });
        });
      } catch (err) {
        cdr?.detectChanges();
        reject(err);
      }
    });
  }

  async printThumbnail(file) {
    let image: any = file;
    if (
      image.name.toUpperCase().includes('.HEIC') ||
      image.name.toUpperCase().includes('.HEIF')
    ) {
      image = await this.convertHeicToJpeg(file);
    }
    return new Promise((resolve) => {
      this.compress(image, true)
        .pipe(take(Application.ONE))
        .subscribe((compressedImage) => {
          resolve(compressedImage);
        });
    });
  }

  async convertHeicToJpeg(e) {
    const blob: File = e;
    const heicTo = (await import('heic-to')).heicTo;
    try {
      return await this.ngZone.run(async () => {
        const conversionResult: any = await heicTo({
          blob,
          type: 'image/jpeg',
          quality: Number(Application.ONE)
        });

        const newName = blob.name.replace(/\.[^/.]+$/, '.jpeg');

        return new File([conversionResult], newName, {
          lastModified: new Date().getTime(),
          type: conversionResult.type
        });
      });
    } catch (err) {
      this.cs.showErrorMessage('Something went wrong. Please try again.');
      throw err;
    }
  }

  async convertAvifToJpeg(blob: File) {
    try {
      return await this.ngZone.run(async () => {
        const img = new Image();
        const url = URL.createObjectURL(blob);

        try {
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load AVIF image'));
            img.src = url;
          });

          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Canvas context not supported');
          }
          ctx.drawImage(img, 0, 0);

          const jpegBlob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create JPEG blob'));
                }
              },
              'image/jpeg',
              Application.ONE
            );
          });

          const newName = blob.name.replace(/\.[^/.]+$/, '.jpeg');

          return new File([jpegBlob], newName, {
            lastModified: new Date().getTime(),
            type: 'image/jpeg',
          });
        } finally {
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      this.cs.showErrorMessage('Something went wrong. Please try again.');
      throw err;
    }
  }

  compress(file: File, isThumbnail): Observable<File> {
    const imageType = file.type || 'image/png';
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return Observable.create((observer) => {
      reader.onload = (ev) => {
        const img = this.createImage(ev);
        const imgWH = img.width > img.height ? img.width : img.height;
        let withHeightRatio =
          imgWH > this.widthHeightMax
            ? this.widthHeightMax / imgWH
            : this.defaultWidthHeightRatio;
        let qualityRatio;
        if (isThumbnail) {
          qualityRatio = Application.THUMBNAIL_QUALITY;
          withHeightRatio = withHeightRatio / Number(Application.THREE);
        } else {
          qualityRatio =
            file.size > this.fileSizeMax
              ? file.size / Number(Application.ZERO_POINT_TWO + Application.ONE)
              : file.size > this.defaultSize
                ? this.defaultWidthHeightRatio
                : this.defaultQualityRatio;
        }
        img.onload = () => {
          if (isPlatformBrowser(this.platformId)) {
            const elem = this.document.createElement('canvas');
            elem.width = img.width * withHeightRatio;
            elem.height = img.height * withHeightRatio;
            const ctx = <CanvasRenderingContext2D>elem.getContext('2d');
            ctx.clearRect(
              Application.ZERO,
              Application.ZERO,
              elem.width,
              elem.height
            );
            ctx.drawImage(
              img,
              Application.ZERO,
              Application.ZERO,
              elem.width,
              elem.height
            );
            ctx.canvas.toBlob(
              (blob) => {
                observer.next(
                  new File([blob], file.name, {
                    type: imageType,
                    lastModified: Date.now()
                  })
                );
              },
              imageType,
              qualityRatio
            );
          }
        };
      };
      reader.onerror = (error) => {
        observer.error(error);
      };
    });
  }

  private createImage(ev) {
    const imageContent = ev.target.result;
    const img = new Image();
    img.src = imageContent;
    return img;
  }

  getBlurredImage(fileName: string) {
    return this.apiCallService.post(
      API_URLS.blurImage,
      { image_name: fileName },
      true
    );
  }

  uploadFileData(apiUrl: string, fileData, emitProgress = false, cdr?: ChangeDetectorRef) {
    const queryParams: any = {
      key: `${fileData.folder}${fileData.fileName}`,
      skipCompression: true
    };
    if (this.cs.adminImagesOverride) {
      queryParams['skipRekognition'] =  true;
    }
    if (fileData.totalChunks) queryParams['totalParts'] = fileData.totalChunks;
    if (fileData.chunkIndex) queryParams['partNumber'] = fileData.chunkIndex;
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${apiUrl}?${queryString}`;
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const request = this.apiCallService.formDataPostType(
          url,
          arrayBuffer,
          true,
          true
        );

        if (request !== null) {
          (request as Observable<any>).subscribe({
            next: (response) => {
              if (response.progressStatus === Application.UPLOADING) {
                if (emitProgress) {
                  observer.next({
                    progressStatus: Application.UPLOADING,
                    progress: response.progress
                  });
                }
              } else if (response.progressStatus === Application.COMPLETED) {
                cdr?.detectChanges();
                observer.next(response);
                observer.complete();
              }
            },
            error: (error) => {
              if (error?.statusCode === ErrorCodes.RESTRICTED_CONTENT) {
                const awsReason = error?.message || 'Restricted content detected.';
              
                const emitRestricted = (image?: string) => {
                  cdr?.detectChanges();
                  observer.next({
                    progressStatus: Application.RESTRICTED,
                    restrictedImage: { awsReason, image }
                  });
                  observer.complete();
                };
              
                if (fileData.file) {
                  const imgReader = new FileReader();
                  imgReader.onload = (e) => {
                    emitRestricted(e.target?.result as string);
                  };
                  imgReader.readAsDataURL(fileData.file);
                } else {
                  emitRestricted('');
                }
                return; // don't fall through to observer.error
              }
              observer.error(error);
            }
          });
        } else {
          observer.error(new Error('Request returned null'));
        }
      };

      reader.onerror = (error) => {
        observer.error(error);
      };

      if (fileData.totalChunks && fileData.chunkIndex) {
        reader.readAsArrayBuffer(fileData.chunk);
      } else {
        reader.readAsArrayBuffer(fileData.file);
      }
    });
  }

  /**
   * Converts S3 Location URL to CDN URL using bucketUrl from environment
   * Example: https://topfan-lite-qa-backend.s3.amazonaws.com/folder/file.jpg
   *          -> https://cdn-www7.topfan.dev/folder/file.jpg
   */
  convertS3LocationToCDNUrl(s3Location: string): string {
    if (!s3Location) {
      return '';
    }
    
    try {
      // Parse the URL to get the path after the bucket domain
      const url = new URL(s3Location);
      const pathname = url.pathname;
      
      // Remove leading slash if present
      const key = pathname.startsWith('/') ? pathname.substring(1) : pathname;
      
      // Construct CDN URL
      return `${environment.s3Config.bucketUrl}${key}`;
    } catch (error) {
      console.error('Error converting S3 Location to CDN URL:', error, s3Location);
      return s3Location; // Return original if parsing fails
    }
  }
}

/**
 * Standalone utility function to convert S3 Location URL to CDN URL
 * Can be used without injecting the service
 */
export function convertS3LocationToCDNUrl(s3Location: string): string {
  if (!s3Location) {
    return '';
  }
  
  try {
    const url = new URL(s3Location);
    const pathname = url.pathname;
    const key = pathname.startsWith('/') ? pathname.substring(1) : pathname;
    return `${environment.s3Config.bucketUrl}${key}`;
  } catch (error) {
    console.error('Error converting S3 Location to CDN URL:', error, s3Location);
    return s3Location;
  }
}
